import * as React from 'react';
import { cn } from '@/lib/utils';
import { FiX } from 'react-icons/fi';

const MagicLinkClose = React.forwardRef(({ className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'magic-link-close',
      // Estilo base
      'absolute right-3 top-3 cursor-pointer leading-none',
      className
    )}
    {...props}
  />
));
MagicLinkClose.displayName = 'MagicLinkClose';

const MagicLinkImage = React.forwardRef(({ className, ...props }, ref) => (
  <img
    ref={ref}
    alt="Imagem do site"
    className={cn(
      'magic-link-image',
      // Estilos Base (Mobile First)
      'h-[180px] w-full flex-shrink-0 bg-primary-lightest object-cover',
      // Estilos para Breakpoint Médio (md)
      'md:h-[132px] md:w-[236px]',
      className
    )}
    {...props}
  />
));
MagicLinkImage.displayName = 'MagicLinkImage';

const MagicLinkBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'magic-link-body',
      // Estilos Base (Mobile First)
      'flex w-[220px] max-w-full flex-grow flex-col gap-3 p-4',
      // Estilos para Breakpoint Médio (md)
      'md:p-4',
      className
    )}
    {...props}
  />
));
MagicLinkBody.displayName = 'MagicLinkBody';

const MagicLinkTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h1
    ref={ref}
    className={cn(
      'magic-link-title',
      // Estilo base
      'm-0 line-clamp-3 break-words text-base font-bold leading-snug text-primary',
      className
    )}
    {...props}
  />
));
MagicLinkTitle.displayName = 'MagicLinkTitle';

const MagicLinkDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'magic-link-description',
      // Estilo base
      'm-0 truncate break-words text-[0.85em] font-light leading-snug text-primary-light',
      className
    )}
    {...props}
  />
));
MagicLinkDescription.displayName = 'MagicLinkDescription';

const MagicLinkUrl = React.forwardRef(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn(
      'magic-link-url',
      // Estilo base
      'mb-0 text-[0.8em] text-secondary',
      className
    )}
    {...props}
  />
));
MagicLinkUrl.displayName = 'MagicLinkUrl';

const MagicLink = React.forwardRef(({ className, magicLink, setShowMagicLink, showClose, variant = 'default', ...props }, ref) => {
  const CloseButton = () => (
    <MagicLinkClose onClick={() => setShowMagicLink(false)}>
      <FiX size={16} />
    </MagicLinkClose>
  );

  const isPostVariant = variant === 'post';

  const handleClick = e => {
    e.stopPropagation();

    if (isPostVariant && magicLink.url) {
      window.open(magicLink.url, '_blank', 'noopener,noreferrer');
    }
  };

  const interactiveProps = isPostVariant
    ? {
        role: 'link', // Indica que é um link
        tabIndex: 0, // Torna ele focável pelo teclado
        onClick: handleClick,
        // Adiciona o suporte para a tecla Enter e Espaço
        onKeyDown: e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e);
          }
        }
      }
    : {};

  return (
    <div
      ref={ref}
      className={cn(
        'c-magic-link',
        // Estilo base
        'animate-fade-in',
        'relative flex w-full flex-wrap items-center',
        'overflow-hidden rounded-[10px] transition-all duration-200 ease-in-out',
        'border border-solid border-primary-lightest',
        // Hover State
        'hover:no-underline hover:shadow-lg',
        // Breakpoint md
        'md:gap-5',
        variant === 'default' && '-mt-4 bg-primary-lightest',
        variant === 'post' && 'mt-[2em] cursor-pointer bg-transparent md:ml-[-5vw] md:w-[calc(100%_+_8.2vw)]',
        className
      )}
      {...interactiveProps}
      {...props}
    >
      {magicLink.image === undefined && (
        <MagicLinkBody>
          {showClose && <CloseButton />}

          <MagicLinkTitle>{magicLink.title}</MagicLinkTitle>
          <MagicLinkUrl>{magicLink.url}</MagicLinkUrl>
          <MagicLinkDescription>{magicLink.description}</MagicLinkDescription>
        </MagicLinkBody>
      )}

      {magicLink.image && (
        <React.Fragment>
          <MagicLinkImage src={magicLink.image} />

          <MagicLinkBody>
            {showClose && <CloseButton />}

            <MagicLinkTitle>{magicLink.title}</MagicLinkTitle>

            <MagicLinkDescription>{magicLink.description}</MagicLinkDescription>
          </MagicLinkBody>
        </React.Fragment>
      )}
    </div>
  );
});
MagicLink.displayName = 'MagicLink';

export { MagicLink };
